import { App, Button, Col, Divider, Form, Input, InputNumber, Radio, Row, Select, Space, Switch, Tag } from "antd";
import type { FormInstance } from "antd";
import { useState } from "react";
import { transportApi, type GstinLookupResult } from "../../api/transportApi";
import { getApiErrorMessage } from "../../utils/apiError";

export const PARTY_TYPE_OPTIONS = [
  { value: "Customer", label: "Transport Customer (Consignor / Consignee)" },
  { value: "Vendor", label: "Transport Supplier / Vendor" },
  { value: "Organization", label: "Customer and Supplier" },
  { value: "Person", label: "Person" }
];

export const PARTY_TYPE_LABELS = Object.fromEntries(PARTY_TYPE_OPTIONS.map((item) => [item.value, item.label]));

export const PartyCompositeFormFields = ({ form, editing }: { form: FormInstance; editing: boolean }) => {
  const { message } = App.useApp();
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<string>();
  const [captcha, setCaptcha] = useState<{ sessionId: string; image: string }>();
  const identityType = Form.useWatch("identityType", form) ?? "GST";

  const fill = (gstin: string, result: GstinLookupResult) => {
    const name = result.legalName || result.name || "";
    form.setFieldsValue({
      gstNumber: gstin,
      name: name || form.getFieldValue("name"),
      registrationType: "Regular",
      pan: gstin.slice(2, 12),
      addressLine1: result.address || form.getFieldValue("addressLine1"),
      country: form.getFieldValue("country") || "India"
    });
    setCaptcha(undefined);
    setStatus([name || "GSTIN found", result.status, result.constitution, result.source ? `Source: ${result.source}` : undefined].filter(Boolean).join(" | "));
  };

  const autoFill = async () => {
    const gstin = String(form.getFieldValue("gstNumber") ?? "").trim().toUpperCase();
    if (gstin.length !== 15) { message.warning("Enter a 15-character GSTIN first."); return; }
    setLoading(true); setStatus(undefined);
    try {
      const local = await transportApi.checkGstin(gstin);
      if (local.found) { fill(gstin, local); return; }
      const portal = await transportApi.getGstinCaptcha();
      setCaptcha({ sessionId: portal.sessionId, image: portal.captchaImage });
      setStatus("Not found locally. Enter the GST portal captcha to fetch taxpayer details.");
    } catch (error) {
      setStatus("GST portal is unavailable. You may retain the GSTIN and enter the party details manually, then verify it later.");
      message.warning(getApiErrorMessage(error, "GST verification service is unavailable"));
    }
    finally { setLoading(false); }
  };

  const submitCaptcha = async () => {
    const gstin = String(form.getFieldValue("gstNumber") ?? "").trim().toUpperCase();
    const answer = String(form.getFieldValue("gstCaptcha") ?? "").trim();
    if (!captcha || !answer) { message.warning("Enter the captcha text."); return; }
    setLoading(true);
    try {
      fill(gstin, await transportApi.lookupGstin({ gstin, captcha: answer, sessionId: captcha.sessionId }));
      form.setFieldValue("gstCaptcha", undefined);
    } catch (error) {
      message.error(getApiErrorMessage(error, "GST portal lookup failed"));
      const portal = await transportApi.getGstinCaptcha().catch(() => null);
      if (portal) setCaptcha({ sessionId: portal.sessionId, image: portal.captchaImage });
      form.setFieldValue("gstCaptcha", undefined);
    } finally { setLoading(false); }
  };

  return <>
    <Divider orientation="left">Transport Party Detail</Divider>
    <Form.Item name="type" label="Party Relationship" rules={[{ required: true }]}><Radio.Group options={PARTY_TYPE_OPTIONS} /></Form.Item>
    <Form.Item name="identityType" label="Identity Type"><Radio.Group options={[{ value: "GST", label: "GST Registered" }, { value: "Aadhaar", label: "Individual / Aadhaar" }]} /></Form.Item>
    {identityType === "GST" ? <>
      <Form.Item name="gstNumber" label="GSTIN"><Space.Compact style={{ width: "100%" }}><Input autoFocus maxLength={15} onChange={(event) => form.setFieldValue("gstNumber", event.target.value.toUpperCase())} /><Button loading={loading} onClick={() => void autoFill()}>Verify & Auto Fill</Button></Space.Compact></Form.Item>
      {captcha && <div className="gst-captcha-box"><button type="button" className="gst-captcha-image-button" title="Refresh captcha" onClick={() => void autoFill()}><img src={captcha.image} alt="GST portal captcha" /></button><Form.Item name="gstCaptcha" label="Captcha" className="gst-captcha-input"><Space.Compact style={{ width: "100%" }}><Input autoComplete="off" /><Button loading={loading} onClick={() => void submitCaptcha()}>Submit</Button></Space.Compact></Form.Item></div>}
    </> : <Form.Item name="aadhaarNumber" label="Aadhaar Number" rules={[{ required: true }, { pattern: /^\d{12}$/, message: "Enter a valid 12-digit Aadhaar number" }]}><Input inputMode="numeric" maxLength={12} /></Form.Item>}
    {status && <Tag color="blue" className="party-form-status">{status}</Tag>}
    <Row gutter={12}><Col span={16}><Form.Item name="name" label="Company Name" rules={[{ required: true }]}><Input /></Form.Item></Col><Col span={8}><Form.Item name="code" label="Code" extra="Auto-generated if blank"><Input /></Form.Item></Col></Row>
    <Row gutter={12}><Col span={12}><Form.Item name="contactPerson" label="Contact Person"><Input /></Form.Item></Col><Col span={12}><Form.Item name="phone" label="Contact No"><Input /></Form.Item></Col></Row>
    <Row gutter={12}><Col span={12}><Form.Item name="email" label="Email"><Input type="email" /></Form.Item></Col><Col span={12}><Form.Item name="registrationType" label="Registration Type"><Select options={["Unregistered", "Regular", "Composite"].map((value) => ({ value, label: value }))} /></Form.Item></Col></Row>
    <Form.Item name="pan" label="PAN"><Input maxLength={10} /></Form.Item>

    <Divider orientation="left">Billing Address</Divider>
    <Form.Item name="addressLine1" label="Address"><Input.TextArea rows={2} /></Form.Item>
    <Row gutter={12}><Col span={12}><Form.Item name="landmark" label="Landmark"><Input /></Form.Item></Col><Col span={12}><Form.Item name="city" label="City"><Input /></Form.Item></Col></Row>
    <Row gutter={12}><Col span={8}><Form.Item name="state" label="State"><Input /></Form.Item></Col><Col span={8}><Form.Item name="country" label="Country"><Input /></Form.Item></Col><Col span={8}><Form.Item name="pincode" label="Pincode"><Input maxLength={6} /></Form.Item></Col></Row>

    <Divider orientation="left">Opening Balance</Divider>
    <Row gutter={12}><Col span={16}><Form.Item name="openingBalance" label="Opening Balance"><InputNumber min={0} style={{ width: "100%" }} /></Form.Item></Col><Col span={8}><Form.Item name="openingBalanceType" label="Balance Type"><Select options={[{ value: "Dr", label: "Debit" }, { value: "Cr", label: "Credit" }]} /></Form.Item></Col></Row>

    <Divider orientation="left">Bank Details</Divider>
    <Form.List name="bankAccounts">{(fields, { add, remove }) => <Space direction="vertical" style={{ width: "100%" }} size={8}>{fields.map((field) => <div className="party-bank-row" key={field.key}><Form.Item {...field} name={[field.name, "bankName"]} label="Bank"><Input /></Form.Item><Form.Item {...field} name={[field.name, "accountNumber"]} label="A/C Number"><Input /></Form.Item><Form.Item {...field} name={[field.name, "ifsc"]} label="IFSC"><Input /></Form.Item><Button danger onClick={() => remove(field.name)}>Remove</Button></div>)}<Button onClick={() => add({ isPrimary: fields.length === 0 })}>Add Bank Detail</Button></Space>}</Form.List>

    <Divider orientation="left">Additional Details</Divider>
    <Row gutter={12}><Col span={12}><Form.Item name="faxNo" label="Fax No"><Input /></Form.Item></Col><Col span={12}><Form.Item name="website" label="Website"><Input /></Form.Item></Col></Row>
    <Form.Item name="note" label="Note"><Input.TextArea rows={2} /></Form.Item>
    {editing && <Form.Item name="isActive" label="Active" valuePropName="checked"><Switch /></Form.Item>}
  </>;
};
